import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import { TextField } from "@/components/base/FormControls";
import { callUsersApi } from "@/lib/usersApi";
import { useToast } from "@/context/ToastContext";
import type { StaffMember } from "@/types";

interface PinModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  member: StaffMember | null;
}

export default function PinModal({ open, onClose, onSaved, member }: PinModalProps) {
  const toast = useToast();
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPin("");
    setConfirm("");
    setError(null);
  }, [open]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!member) return;
    if (!/^[0-9]{6}$/.test(pin)) {
      setError("El PIN debe tener exactamente 6 dígitos.");
      return;
    }
    if (pin !== confirm) {
      setError("El PIN y su confirmación no coinciden.");
      return;
    }

    setSubmitting(true);
    const result = await callUsersApi({ action: "set_pin", id: member.id, pin });
    setSubmitting(false);

    if (!result.ok) {
      setError(result.error ?? "No se pudo cambiar el PIN.");
      return;
    }
    toast.success(`PIN actualizado para ${member.full_name}.`);
    onSaved();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title="Cambiar PIN"
      subtitle={
        member
          ? `Define un nuevo PIN de 6 dígitos para ${member.full_name} ${member.last_name}.`
          : undefined
      }
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="pin-form"
            disabled={submitting}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-70"
          >
            {submitting ? <i className="ri-loader-4-line animate-spin text-base"></i> : null}
            Guardar PIN
          </button>
        </>
      }
    >
      <form id="pin-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        <TextField
          label="Nuevo PIN"
          name="pin"
          value={pin}
          onChange={(value) => setPin(value.replace(/[^0-9]/g, "").slice(0, 6))}
          placeholder="000000"
          inputMode="numeric"
          maxLength={6}
          required
        />
        <TextField
          label="Confirmar PIN"
          name="pin_confirm"
          value={confirm}
          onChange={(value) => setConfirm(value.replace(/[^0-9]/g, "").slice(0, 6))}
          placeholder="000000"
          inputMode="numeric"
          maxLength={6}
          required
        />
        <p className="text-xs text-foreground-400">
          El PIN se guarda cifrado con hash; nunca en texto plano.
        </p>
        {error ? (
          <div className="animate-shake flex items-start gap-2.5 rounded-md border border-primary-300 bg-primary-50 px-3.5 py-3 text-sm text-primary-800">
            <span className="mt-0.5 flex h-4 w-4 items-center justify-center">
              <i className="ri-error-warning-line text-base"></i>
            </span>
            <span className="flex-1 leading-snug">{error}</span>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}